import * as React from "react";

// This is needed due to react pointer events reaching listeners that are not an ancestor of the currentTarget 
function cameThroughCurrentTarget(e: React.SyntheticEvent) {
    const path = (e.nativeEvent).composedPath?.() as EventTarget[] | undefined;
    return path ? path.includes(e.currentTarget) : (e.currentTarget).contains(e.target as HTMLElement);
}

type UseSwipeIndexProps = {
    index: number,
    setIndex: (i: number) => void;
    count: number;
    thresholdPx?: number;
    maxVerticalRatio?: number;
};

export function useSwipeIndex({
    index,
    setIndex,
    count,
    thresholdPx = 60,
}: UseSwipeIndexProps) {
    const [dragX, setDragX] = React.useState(0);
    const tracking = React.useRef(false);
    const start = React.useRef({ x: 0, y: 0 });
    const activePointerId = React.useRef<number | null>(null);

    const onPointerDown = React.useCallback((e: React.PointerEvent) => {
        if (!cameThroughCurrentTarget(e)) return;

        // only primary button / touch contact
        if (e.pointerType === "mouse" && e.button !== 0) return;

        tracking.current = true;
        activePointerId.current = e.pointerId;
        start.current = { x: e.clientX, y: e.clientY };
        setDragX(0);
    }, []);

    const onPointerMove = React.useCallback(
        (e: React.PointerEvent) => {
            if (!cameThroughCurrentTarget(e)) return;
            if (!tracking.current) return;
            if (activePointerId.current !== e.pointerId) return;

            const dx = e.clientX - start.current.x;

            setDragX(dx);

            // keep receiving move events even if pointer leaves the element
            (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
        },
        []
    );

    const finish = React.useCallback(() => {
        const dx = dragX;
        tracking.current = false;
        activePointerId.current = null;
        setDragX(0);

        if (Math.abs(dx) < thresholdPx) return;

        // swipe left => next card (index + 1), swipe right => previous (index - 1)
        const dir = dx < 0 ? 1 : -1;
        const next = Math.max(0, Math.min(count - 1, index + dir));
        if (next !== index) setIndex(next);
    }, [dragX, thresholdPx, count, index, setIndex]);

    const onPointerUp = React.useCallback(
        (e: React.PointerEvent) => {
            if (!cameThroughCurrentTarget(e)) return;
            if (activePointerId.current !== e.pointerId) return;
            finish();
        },
        [finish]
    );

    const onPointerCancel = React.useCallback(
        (e: React.PointerEvent) => {
            if (!cameThroughCurrentTarget(e)) return;
            if (activePointerId.current !== e.pointerId) return;
            finish();
        },
        [finish]
    );

    return {
        dragX,
        bind: {
            onPointerDown,
            onPointerMove,
            onPointerUp,
            onPointerCancel,
            // Critical for touch: prevents browser from “handling” horizontal pan.
            // Still allows vertical scroll because we early-cancel on mostly-vertical.
            style: { touchAction: "pan-y" as const },
        },
    };
}